import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { appLogout, injectSlice } from '@bonanza-social/toolkit/state';
import { TransactionHistoryState } from './transactionHistory.types';
import HistoryService from '../../api/HistoryService';
import { FetchHistoryParams } from '../shared/history.types';
import { isAfterTimestamp } from '@bonanza-social/toolkit/utils';
import { DEFAULT_PAGE_SIZE } from '../shared/history.constants';

export const fetchTransactionHistory = createAsyncThunk(
  'transactionHistory/fetch',
  async ({ startDate, pageNumber = 1, replace }: FetchHistoryParams) => {
    const response = await HistoryService.getTransactionHistory({
      params: {
        pageNumber: replace ? 1 : pageNumber,
        startDate,
        pageSize: DEFAULT_PAGE_SIZE,
      },
    });

    return response;
  },
);

const initialState: TransactionHistoryState = {
  data: undefined,
  dataState: 'idle',
  lastTimestamp: undefined,
};

const transactionHistory = createSlice({
  name: 'transactionHistory',
  initialState,
  reducers: {
    resetTransactionsLastTimestamp(state) {
      state.lastTimestamp = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTransactionHistory.pending, (state) => {
      state.dataState = 'pending';
    });
    builder.addCase(fetchTransactionHistory.fulfilled, (state, action) => {
      const { replace, startDate } = action.meta.arg;
      state.dataState = 'fulfilled';
      state.lastTimestamp = startDate;
      if (replace) {
        state.data = action.payload;
      } else {
        const oldItems = state.data?.items;
        const newItems = action.payload.items;
        state.data = action.payload;
        if (oldItems) {
          state.data.items = [...oldItems, ...newItems];
        }
      }
    });
    builder.addCase(fetchTransactionHistory.rejected, (state) => {
      state.dataState = 'rejected';
    });

    builder.addCase(appLogout, () => {
      return initialState;
    });
  },
});

const withTransactionHistory = injectSlice({
  reducerPath: 'transactionHistory',
  reducer: transactionHistory.reducer,
});

const selectAllItems = withTransactionHistory.selector(
  (state) => state.transactionHistory.data?.items,
);

export const selectTransactionHistoryLastTimestamp =
  withTransactionHistory.selector(
    (state) => state.transactionHistory.lastTimestamp,
  );

export const selectTransactionHistoryItems = createSelector(
  [selectAllItems, (_, timestamp) => timestamp],
  (allItems, timestamp) =>
    (allItems ?? []).filter((item) =>
      isAfterTimestamp(item.createdAt, timestamp),
    ),
);

export const selectTransactionHistoryDataState =
  withTransactionHistory.selector(
    (state) => state.transactionHistory.dataState,
  );

export const selectTransactionHistoryHasNextPage =
  withTransactionHistory.selector(
    (state) => state.transactionHistory.data?.hasNextPage,
  );

export const selectTransactionHistoryPageNumber =
  withTransactionHistory.selector(
    (state) => state.transactionHistory.data?.pageNumber,
  );

export const selectHasPaymentMethodsAwaitingDocumentReview =
  withTransactionHistory.selector(
    (state) =>
      state.transactionHistory.data?.hasPaymentMethodsAwaitingDocumentReview,
  );

export const selectHasPaymentMethodsAwaitingDocumentVerification =
  withTransactionHistory.selector(
    (state) =>
      state.transactionHistory.data
        ?.hasPaymentMethodsWithRequiredDocumentVerification,
  );

export const { resetTransactionsLastTimestamp } = transactionHistory.actions;

export default transactionHistory.reducer;

declare global {
  interface LazyLoadedSlices {
    transactionHistory: ReturnType<typeof transactionHistory.reducer>;
  }
}
